import {
  Box,
  Container,
  Flex,
  HStack,
  Icon,
  Image,
  Link,
  Text,
  VStack,
  SimpleGrid,
  Heading,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { FaFacebookF, FaInstagram, FaLinkedinIn } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import type { IconType } from 'react-icons';

const APP_URL = 'https://app.wagerwatchdog.com';
const DISCORD_URL = 'https://discord.com/invite/MUpH2jqg94';
const SUPPORT_PORTAL_URL = 'https://support.wagerwatchdog.com';
const DOCS_URL = 'https://docs.wagerwatchdog.com';

const SOCIAL_LINKS: { href: string; label: string; icon: IconType }[] = [
  {
    href: 'https://www.linkedin.com/company/wagerwatchdogapp/',
    label: 'LinkedIn',
    icon: FaLinkedinIn,
  },
  {
    href: 'https://x.com/wagerwatchdog',
    label: 'X',
    icon: FaXTwitter,
  },
  {
    href: 'https://www.instagram.com/wagerwatchdog',
    label: 'Instagram',
    icon: FaInstagram,
  },
  {
    href: 'https://www.facebook.com/profile.php?id=61591429358747',
    label: 'Facebook',
    icon: FaFacebookF,
  },
];

type FooterLink = {
  label: string;
  to?: string;
  href?: string;
  external?: boolean;
};

type FooterSection = {
  title: string;
  links: FooterLink[];
};

const FOOTER_SECTIONS: FooterSection[] = [
  {
    title: 'Explore',
    links: [
      { label: 'Expert Ratings', to: '/ratings' },
      { label: 'About Us', to: '/about' },
      { label: 'Blog', to: '/blog' },
    ],
  },
  {
    title: 'Support',
    links: [
      { label: 'Contact', to: '/contact' },
      { label: 'Support', href: SUPPORT_PORTAL_URL, external: true },
      { label: 'Help Docs', href: DOCS_URL, external: true },
      { label: 'Join Discord', href: DISCORD_URL, external: true },
    ],
  },
  {
    title: 'App',
    links: [
      { label: 'Log In', href: `${APP_URL}/login` },
      { label: 'Claim Your Profile', to: '/claim' },
    ],
  },
  {
    title: 'Legal',
    links: [
      { label: 'Responsible Gaming', to: '/responsible-gaming' },
      { label: 'Privacy Policy', to: '/privacy' },
      { label: 'Terms of Service', to: '/terms' },
      { label: 'Refund Policy', to: '/refunds' },
      { label: 'Subprocessors', to: '/subprocessors' },
      { label: 'llms.txt', href: '/llms.txt' },
    ],
  },
];

function FooterLinkList({ title, links }: FooterSection) {
  return (
    <VStack align={{ base: 'center', md: 'start' }} spacing={3}>
      <Heading
        as="h4"
        fontSize="sm"
        fontWeight="semibold"
        color="gray.400"
        textTransform="uppercase"
        letterSpacing="wider"
      >
        {title}
      </Heading>
      {links.map((item) =>
        item.to ? (
          <Link
            key={item.label}
            as={RouterLink}
            to={item.to}
            color="gray.400"
            fontSize="sm"
            _hover={{ color: 'white' }}
          >
            {item.label}
          </Link>
        ) : (
          <Link
            key={item.label}
            href={item.href}
            color="gray.400"
            fontSize="sm"
            _hover={{ color: 'white' }}
            isExternal={item.external}
          >
            {item.label}
          </Link>
        ),
      )}
    </VStack>
  );
}

export default function Footer() {
  return (
    <Box
      as="footer"
      py={12}
      borderTop="1px solid"
      borderColor="whiteAlpha.100"
    >
      <Container maxW="container.content">
        <SimpleGrid
          columns={{ base: 1, sm: 2, lg: 5 }}
          spacing={{ base: 8, md: 10, lg: 12 }}
          mb={10}
        >
          <VStack
            align={{ base: 'center', md: 'start' }}
            spacing={4}
            gridColumn={{ base: '1', sm: '1 / -1', lg: 'auto' }}
          >
            <HStack spacing={3}>
              <Image
                src="/assets/images/ww_logo_dark.svg"
                alt="WagerWatchdog"
                h="28px"
              />
            </HStack>
            <Text
              color="gray.500"
              fontSize="sm"
              textAlign={{ base: 'center', md: 'left' }}
            >
              Independent performance ratings for sports betting experts.
              Performance over popularity.
            </Text>
            <HStack spacing={1}>
              {SOCIAL_LINKS.map(({ href, label, icon }) => (
                <Link
                  key={label}
                  href={href}
                  isExternal
                  target="_blank"
                  rel="noopener noreferrer"
                  display="inline-flex"
                  alignItems="center"
                  justifyContent="center"
                  w="36px"
                  h="36px"
                  borderRadius="md"
                  color="gray.400"
                  _hover={{
                    color: 'white',
                    bg: 'whiteAlpha.100',
                    textDecoration: 'none',
                  }}
                  aria-label={`WagerWatchdog on ${label}`}
                >
                  <Icon as={icon} boxSize={5} />
                </Link>
              ))}
            </HStack>
            <Text
              color="gray.600"
              fontSize="xs"
              textAlign={{ base: 'center', md: 'left' }}
            >
              This platform is meant for entertainment and analytic purposes
              only. If you or someone you know has a gambling problem and wants
              help, call 1-800 GAMBLER. WagerWatchdog is intended for adult use
              only.{' '}
              <Link
                as={RouterLink}
                to="/responsible-gaming"
                color="gray.500"
                textDecoration="underline"
                _hover={{ color: 'gray.400' }}
              >
                Responsible Gaming
              </Link>
            </Text>
          </VStack>

          {FOOTER_SECTIONS.map((section) => (
            <FooterLinkList key={section.title} {...section} />
          ))}
        </SimpleGrid>

        <Flex
          direction={{ base: 'column', md: 'row' }}
          justify="space-between"
          align="center"
          gap={4}
          pt={8}
          borderTop="1px solid"
          borderColor="whiteAlpha.50"
        >
          <Text color="gray.500" fontSize="xs">
            &copy; 2022&ndash;{new Date().getFullYear()} IvoryShoulder, LLC DBA
            WagerWatchdog. All rights reserved.
          </Text>
          <Link
            as={RouterLink}
            to="/responsible-gaming"
            color="gray.600"
            fontSize="xs"
            _hover={{ color: 'gray.400' }}
          >
            WagerWatchdog does not facilitate or encourage gambling. Use
            responsibly.
          </Link>
        </Flex>
      </Container>
    </Box>
  );
}
